/** Field walk-through mode (§6): a separate layout, not a squeezed desktop. Thumb
 *  targets ≥56px, base type 19px, dark glare header, offline as a stated mode.
 *  One card per line in walk order; Verify is the biggest control on every card. */
import { useEffect, useRef, useState } from 'preact/hooks';
import { getRecord, putRecord, createRecord } from '../../core/db';
import { nowIso } from '../../core/ids';
import { fmtQty } from '../../core/money';
import type { InventoryItem, TakeoffLine } from '../../core/types';
import { jobBundleSig, loadJobBundle, onlineSig, pricesSig, settingsSig, toast } from '../state';
import { queueVoiceNote, processOutbox, pendingVoiceCount } from '../../ai/outbox';
import { SourceChip } from '../bits';

export function FieldView({ jobId }: { jobId: string }) {
  const [voiceN, setVoiceN] = useState(0);
  const [recording, setRecording] = useState(false);
  const recRef = useRef<MediaRecorder | null>(null);
  const chunksRef = useRef<Blob[]>([]);

  useEffect(() => {
    void loadJobBundle(jobId);
    void pendingVoiceCount(jobId).then(setVoiceN);
  }, [jobId]);

  // reconnects process the queue right here in the field
  useEffect(() => {
    const onUp = () => { void runOutbox(); };
    window.addEventListener('online', onUp);
    return () => window.removeEventListener('online', onUp);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [jobId]);

  const b = jobBundleSig.value;
  const prices = pricesSig.value;
  const user = settingsSig.value?.currentUser ?? 'david';
  if (!b || b.job.id !== jobId) return <div class="muted">Loading walk-through…</div>;
  const { job, lines, inventory } = b;
  const priceBy = new Map(prices.map((p) => [p.itemId, p]));
  const verifs = [...lines.map((l) => l.verification), ...inventory.map((i) => i.verification)];
  const done = verifs.filter((v) => v.verified).length;

  async function runOutbox() {
    const r = await processOutbox(user);
    setVoiceN(await pendingVoiceCount(jobId));
    if (r.processed) { toast(`${r.processed} voice note(s) transcribed — results in the review queue`); await loadJobBundle(jobId); }
  }

  async function verifyLine(l: TakeoffLine, v: boolean) {
    await putRecord('takeoffLines', { ...l, verification: { verified: v, by: v ? user : undefined, at: v ? nowIso() : undefined, note: l.verification.note } });
    await loadJobBundle(jobId);
  }
  async function verifyInv(it: InventoryItem, v: boolean) {
    await putRecord('inventoryItems', { ...it, verification: { verified: v, by: v ? user : undefined, at: v ? nowIso() : undefined, note: it.verification.note } });
    await loadJobBundle(jobId);
  }
  async function stepLine(l: TakeoffLine, delta: number) {
    const qty = Math.max(0, l.qty + delta);
    await putRecord('takeoffLines', {
      ...l, qty,
      provenance: { ...l.provenance, source: 'manual', setBy: user, setAt: nowIso() },
      verification: { ...l.verification, verified: false },
    });
    await loadJobBundle(jobId);
  }
  async function noteLine(l: TakeoffLine, note: string) {
    await putRecord('takeoffLines', { ...l, verification: { ...l.verification, note: note || undefined } });
  }
  async function photoToLine(l: TakeoffLine, ev: Event) {
    const f = (ev.target as HTMLInputElement).files?.[0];
    (ev.target as HTMLInputElement).value = '';
    if (!f) return;
    await createRecord('photos', {
      jobId, bytes: await f.arrayBuffer(), mime: f.type || 'image/jpeg',
      capturedAt: nowIso(), caption: l.label, linkedTo: { kind: 'takeoff', id: l.id },
    });
    toast(`Photo attached to “${l.label}”`);
  }

  async function micDown() {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      chunksRef.current = [];
      const rec = new MediaRecorder(stream);
      rec.ondataavailable = (e) => { if (e.data.size) chunksRef.current.push(e.data); };
      rec.onstop = () => {
        stream.getTracks().forEach((t) => t.stop());
        void (async () => {
          const blob = new Blob(chunksRef.current, { type: rec.mimeType || 'audio/webm' });
          if (blob.size < 200) return; // a tap, not a note
          await queueVoiceNote(jobId, blob);
          setVoiceN(await pendingVoiceCount(jobId));
          if (navigator.onLine) { void runOutbox(); }
          else toast('Voice note saved on the phone — it transcribes when you are back on signal');
        })();
      };
      rec.start();
      recRef.current = rec;
      setRecording(true);
    } catch {
      toast('Microphone not available — type the note on the line instead');
    }
  }
  function micUp() {
    recRef.current?.state === 'recording' && recRef.current.stop();
    recRef.current = null;
    setRecording(false);
  }

  return (
    <div class="field-page">
      <header class="fm-head">
        <div style="display:flex;justify-content:space-between;align-items:center;gap:8px;">
          <div>
            <div class="fm-title">{job.title}</div>
            <div class="fm-sub num">{done} / {verifs.length} verified · <span class="mono">{job.bidNumber}</span></div>
          </div>
          <a href={`#/job/${jobId}`} class="fm-exit" aria-label="Back to the desk workspace">Desk</a>
        </div>
        <div style="display:flex;justify-content:space-between;align-items:center;gap:8px;margin-top:6px;">
          <div class="flagline" role="img" aria-label={`${done} of ${verifs.length} verified`}>
            {verifs.slice(0, 24).map((v, i) => <span key={i} class={`tie ${v.verified ? 'tie-v' : ''}`} style="border-color:rgba(255,255,255,.6);" />)}
            {verifs.length > 24 && <span class="small" style="color:rgba(255,255,255,.8);">+{verifs.length - 24}</span>}
          </div>
          {!onlineSig.value
            ? <span class="fm-off">OFFLINE — edits saved on phone</span>
            : voiceN > 0 ? <span class="fm-off" style="background:#fff;">{voiceN} note(s) queued</span> : null}
        </div>
      </header>

      <div class="fm-list">
        {voiceN > 0 && !onlineSig.value && (
          <div class="fm-queuebar" role="status">🎙 {voiceN} voice note(s) queued — transcribe when you're back on signal.</div>
        )}
        <div class="fm-zonehead">Measured work</div>
        {lines.map((l) => {
          const p = l.priceItemId ? priceBy.get(l.priceItemId) : undefined;
          const unit = l.custom?.unit ?? p?.unit ?? '';
          const v = l.verification.verified;
          return (
            <div class={`fm-card ${v ? 'fm-done' : ''}`} key={l.id}>
              <div class="fm-item">{l.custom?.name ?? p?.name ?? l.label}
                {' '}{l.provenance.source === 'ai' && <span class="srcchip srcchip-ai">AI draft</span>}
              </div>
              <div class="fm-meta">
                {fmtQty(l.qty, unit)} {unit} · <SourceChip source={l.provenance.source} />
                {v && l.verification.at ? ` · verified ${new Date(l.verification.at).toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' })}` : ''}
              </div>
              <div class="fm-row">
                <span class="fm-step" role="group" aria-label={`Adjust quantity for ${l.label}`}>
                  <button class="fm-stepbtn" aria-label="Less" onClick={() => void stepLine(l, -stepFor(unit))}>−</button>
                  <b class="num">{fmtQty(l.qty, unit)}</b>
                  <button class="fm-stepbtn" aria-label="More" onClick={() => void stepLine(l, stepFor(unit))}>+</button>
                </span>
                <button class={`fm-verify ${v ? 'fm-verify-on' : ''}`} aria-pressed={v}
                  onClick={() => void verifyLine(l, !v)}>
                  <span class={`tie ${v ? 'tie-v' : ''}`} style={v ? 'border-color:#fff;' : ''} /> {v ? 'Verified' : 'Verify'}
                </button>
              </div>
              {!v && (
                <div class="fm-aux">
                  <label class="fm-auxbtn">📷 Photo → this line
                    <input type="file" accept="image/*" capture="environment" style="display:none;"
                      onChange={(e) => void photoToLine(l, e)} aria-label={`Photo for ${l.label}`} />
                  </label>
                  <input class="fm-note" placeholder="Add note…" defaultValue={l.verification.note ?? ''}
                    aria-label={`Note for ${l.label}`}
                    onChange={(e) => void noteLine(l, (e.target as HTMLInputElement).value)} />
                </div>
              )}
            </div>
          );
        })}

        <div class="fm-zonehead">Site inventory</div>
        {inventory.map((it) => {
          const v = it.verification.verified;
          return (
            <div class={`fm-card ${v ? 'fm-done' : ''}`} key={it.id}>
              <div class="fm-item">{it.cat}{it.species ? ` — ${it.species}` : ''}</div>
              <div class="fm-meta">{fmtQty(it.qty, 'each')} · {it.cond}{it.notes ? ` · ${it.notes}` : ''}</div>
              <div class="fm-row">
                <span />
                <button class={`fm-verify ${v ? 'fm-verify-on' : ''}`} aria-pressed={v}
                  onClick={() => void verifyInv(it, !v)}>
                  <span class={`tie ${v ? 'tie-v' : ''}`} style={v ? 'border-color:#fff;' : ''} /> {v ? 'Verified' : 'Verify'}
                </button>
              </div>
            </div>
          );
        })}

        {done === verifs.length && verifs.length > 0 && (
          <div class="fm-alldone">All {verifs.length} verified — back at the truck, issue the updated proposal from the desk view.</div>
        )}
      </div>

      <div class="fm-dock">
        <a class="fm-dockbtn" href={`#/job/${jobId}`}>Desk view</a>
        <button
          class={`fm-mic ${recording ? 'fm-mic-on' : ''}`}
          onMouseDown={() => void micDown()} onMouseUp={micUp} onMouseLeave={micUp}
          onTouchStart={(e) => { e.preventDefault(); void micDown(); }} onTouchEnd={micUp}
          aria-label="Hold to record a voice note"
        >🎙<span>{recording ? 'REC' : 'HOLD'}</span></button>
        <span class="fm-dockbtn" style="visibility:hidden;" aria-hidden="true" />
      </div>
    </div>
  );
}

function stepFor(unit: string): number {
  return unit === 'sq ft' ? 50 : unit === 'lin ft' ? 5 : 1;
}
